// apps/web/src/widgets/visible-when.ts
//
// 11.6 D2 — isVisibleById LIFTED here from summary-checklist/visible-when.ts so the gated warning static-note (Plan 06) and the checklist share ONE fail-OPEN implementation. Contract preserved verbatim (Pitfall 7 default-true on unresolved/drift).
//
// Mirrors the use-source-selection.ts lift precedent: a shared widget helper in
// the widgets root, re-exported from its original folder location. The
// summary-checklist renderer + its Phase-6/9 tests import this function
// UNCHANGED through `summary-checklist/visible-when.ts`'s re-export shim.

import type { Constraint, RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import type { WidgetInstanceId, WidgetState } from "./widget-renderer-props.js";

/**
 * Resolve a visibility constraint by document-position id and evaluate its
 * `when` clause against widget state. Pitfall 7 default-true: unresolved or
 * non-visibility-kind references return `true` (item visible) so a stale id
 * or schema drift doesn't silently hide content.
 *
 * Lookup scheme: ids are derived from document position in `recipe.constraints[]`
 * as `constraint-${index}` (Plan 09-03 Q-02 resolution). The migration script
 * in `packages/shared/scripts/migrate-visibleWhen-to-constraint.ts` emits ids
 * of the form `vc-{widget.id}-{item.id}-{anyOfIdx}` at MIGRATION TIME, but the
 * renderer resolves at READ TIME via document position; the migration script's
 * scheme is informational (helps humans inspect the migration output) — the
 * canonical resolution path is index-derived.
 *
 * To bridge that: this helper accepts EITHER scheme and falls back to a name
 * lookup against the show.pathInState pattern when index parsing fails. Both
 * paths end at the same Constraint object.
 *
 * @param constraintId  The reference stored on `SummaryChecklistItem.visibilityConstraintId`.
 *                       `undefined` means no visibility predicate (always visible).
 * @param recipe         Recipe definition carrying the `constraints[]` array.
 * @param widgetStates   The in-memory map of widget instance state.
 * @returns true if the item should be visible; false otherwise.
 */
export function isVisibleById(
  constraintId: string | undefined,
  recipe: RecipeDefinitionV1,
  widgetStates: ReadonlyMap<WidgetInstanceId, WidgetState>,
): boolean {
  // Phase 6 D-04 contract: no predicate → always visible.
  if (constraintId === undefined) return true;

  const constraints = recipe.constraints ?? [];

  // Resolve by document-position id (`constraint-N`) first — the renderer's
  // canonical scheme per Plan 09-03 Q-02. If that fails (e.g., migration-
  // emitted `vc-...` id), fall back to a name-pattern match.
  let constraint: Constraint | undefined;
  const indexMatch = /^constraint-(\d+)$/.exec(constraintId);
  if (indexMatch !== null) {
    const idx = Number(indexMatch[1]);
    constraint = constraints[idx];
  }
  if (constraint === undefined) {
    // Migration-script id scheme: `vc-{widgetId}-{itemId}-{anyOfIdx}`. We can't
    // round-trip the exact widget/item without more context (the constraint id
    // alone doesn't carry the recipe coordinates), so we scan for the visibility
    // constraint whose show.pathInState contains the parsed remainder as a WHOLE
    // segment (or whose widgetInstanceId equals it exactly). The parse extracts
    // the remainder between the `vc-` prefix and the trailing `-N`.
    const vcMatch = /^vc-(.+)-(\d+)$/.exec(constraintId);
    if (vcMatch !== null) {
      const remainder = vcMatch[1];
      if (remainder !== undefined) {
        // WR-01: EXACT-segment match, never loose `.includes`. A loose
        // substring scan can resolve the WRONG visibility constraint when
        // multiple exist (e.g. remainder "card" matching both
        // widgetInstanceId "card" AND "card-2"), and for a Mission-Critical
        // safety-warning gate a mis-resolution that HIDES a warning is the
        // dangerous direction. We require `remainder` to equal either a whole
        // `pathInState` segment (split on `.`/`[`/`]`) or the whole
        // `widgetInstanceId`. If MORE THAN ONE constraint still matches, we
        // refuse to guess and leave `constraint` undefined → fail-OPEN
        // default-true below (SHOWS the warning) rather than gate on an
        // unintended source widget. The canonical `constraint-N` index path
        // above is unaffected.
        const matches = constraints.filter((c) => {
          if (c.kind !== "visibility") return false;
          const segments = c.show.pathInState.split(/[.[\]]/).filter((s) => s.length > 0);
          return segments.includes(remainder) || remainder === c.show.widgetInstanceId;
        });
        if (matches.length === 1) {
          constraint = matches[0];
        }
      }
    }
  }

  // Pitfall 7 default-true: unresolved → visible (don't silently hide on drift).
  if (constraint === undefined) return true;

  // Non-visibility kind → defensive default-true (Pitfall 7).
  if (constraint.kind !== "visibility") return true;

  // Evaluate the `when` clause. Predicate present + state absent → hide
  // (matches the legacy isVisible semantics: predicate present, source
  // unmounted → false).
  const sourceState = widgetStates.get(constraint.when.ref.widgetInstanceId);
  if (sourceState === undefined) return false;

  // Read the source-state value at the path. For Phase 6 + 9 in v1, paths are
  // single-segment ("selected", "checkedItems", "viewMode"). We narrow via a
  // structural key access — `noPropertyAccessFromIndexSignature` requires
  // bracket form for unknown keys.
  const path = constraint.when.ref.pathInState;
  const value: unknown = (sourceState as Readonly<Record<string, unknown>>)[path];

  // For array-typed selected state (option-set), the legacy semantics treated
  // the comparison as "array INCLUDES the equals value". Preserve that for
  // backward compatibility while ALSO supporting direct deep-equal for non-array
  // values (the generalized visibility kind allows any equals shape).
  if (Array.isArray(value)) {
    return value.some((v) => deepEqual(v, constraint.when.equals));
  }
  return deepEqual(value, constraint.when.equals);
}

// WR-02: defense-in-depth recursion bound. `constraint.when.equals` is
// `z.unknown()` at the constraint-schema layer (no depth/size cap), so a
// deeply-nested author-controlled `equals` could otherwise drive recursion to
// the data's full depth. Past MAX_DEPTH we return `false` (NOT-equal) — the
// fail-SAFE direction: an over-deep comparison must never spuriously claim
// equality and flip a safety-warning gate visible/hidden. Normal-depth inputs
// (the common path is single-segment "selected"/"checkedItems" comparands and
// shallow object `equals`) are entirely unaffected by this ceiling.
const MAX_DEPTH = 32;

// Inline recursive deep-equal — handles primitives, arrays, plain objects.
// Sufficient for visibility comparands which are JSON-derived (no Maps/Sets,
// no Dates). Iterative would be marginally faster but recursive is clearer
// for the bounded shapes here (predicate values are author-typed JSON).
function deepEqual(a: unknown, b: unknown, depth = 0): boolean {
  if (a === b) return true;
  // Bound author-controlled `equals` nesting (defense-in-depth). Strictly
  // reference-unequal operands deeper than MAX_DEPTH are treated as not-equal.
  if (depth > MAX_DEPTH) return false;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i], depth + 1)) return false;
    }
    return true;
  }
  if (Array.isArray(b)) return false;

  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  if (aKeys.length !== bKeys.length) return false;
  const aRec = a as Readonly<Record<string, unknown>>;
  const bRec = b as Readonly<Record<string, unknown>>;
  for (const k of aKeys) {
    if (!Object.hasOwn(bRec, k)) return false;
    if (!deepEqual(aRec[k], bRec[k], depth + 1)) return false;
  }
  return true;
}
